import { FormConfig } from "@/types/FormConfig";

export function applyUrlOverrides(
  config: FormConfig,
  searchParams: URLSearchParams,
): FormConfig {
  const defaultsOverrides: Record<string, string> = {};

  searchParams.forEach((value, key) => {
    defaultsOverrides[key] = value;
  });

  const updatedComponents = config.components.map((component) => {
    if (
      !["amount", "donation_intent", "donation_source"].includes(component.name)
    ) {
      return component;
    }

    const componentDefaults =
      (component.config as unknown as Record<string, unknown> | undefined)
        ?.defaults || {};
    const updatedDefaults = { ...componentDefaults };

    Object.keys(defaultsOverrides).forEach((key) => {
      const isAmountKey =
        component.name === "amount" &&
        [
          "donation-type",
          "one-off-donation-amount",
          "monthly-donation-amount",
        ].includes(key);
      const isIntentKey =
        component.name === "donation_intent" && key === "donation-intent";
      const isSourceKey =
        component.name === "donation_source" && key === "donation-source";

      if (!isAmountKey && !isIntentKey && !isSourceKey) {
        return;
      }

      if (
        ["one-off-donation-amount", "monthly-donation-amount"].includes(key)
      ) {
        const amount = parseFloat(defaultsOverrides[key]);
        if (!isNaN(amount)) {
          (updatedDefaults as Record<string, unknown>)[key] = amount;
        }
        return;
      }

      if (["donation-intent"].includes(key)) {
        const validIntents =
          ((component.config as unknown as Record<string, unknown>)[
            "donation-intent"
          ] as Array<{ key: string }>) || [];
        const isValid = validIntents.some(
          (intent) => intent.key === defaultsOverrides[key],
        );

        if (isValid) {
          (updatedDefaults as Record<string, unknown>)[key] =
            defaultsOverrides[key];
        }
        return;
      }

      if (["donation-source"].includes(key)) {
        const validOptions =
          ((component.config as unknown as Record<string, unknown>)[
            "donation-source-options"
          ] as Array<{ value: string }>) || [];
        const isValid = validOptions.some(
          (option) => option.value === defaultsOverrides[key],
        );

        if (isValid) {
          (updatedDefaults as Record<string, unknown>)[key] =
            defaultsOverrides[key];
        }
        return;
      }

      (updatedDefaults as Record<string, unknown>)[key] =
        defaultsOverrides[key];
    });

    return {
      ...component,
      config: {
        ...component.config,
        defaults: updatedDefaults,
      },
    } as typeof component;
  });

  return { ...config, components: updatedComponents };
}
